import { useEffect, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
} from "react-icons/fi";
import { IoMdSearch } from "react-icons/io";
import { useLocation, useNavigate } from "react-router-dom";
import { LIVE_URL } from "../../../config/config";
import useAuthToken from "../../../hooks/useAuthToken";
import styles from "./css/Payment.module.scss";
import PaymentTable from "./datatable/PaymentTable";

const Payment = () => {
  const { token } = useAuthToken();
  const navigate = useNavigate();
  const location = useLocation();
  // define state
  const [searchTerm, setSearchTerm] = useState(
    new URLSearchParams(location.search).get("search") || ""
  );
  const [planName, setPlanName] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [userId, setUserId] = useState("");
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [limit, setLimit] = useState(10);
  const [payments, setPayments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("DESC");
  const [sortField, setSortField] = useState("id");

  useEffect(() => {
    const fetchPayment = async () => {
      try {
        // setLoading(true);
        const queryParams = new URLSearchParams();
        if (searchTerm) {
          queryParams.set("search", searchTerm);
        }
        if (planName) {
          queryParams.set("plan_name", planName);
        }
        if (transactionId) {
          queryParams.set("revenuecat_transaction_id", transactionId);
        }
        if (userId) {
          queryParams.set("user_id", userId);
        }
        queryParams.set("page", currentPage);
        queryParams.set("pageSize", entriesPerPage);
        queryParams.set("sort_by", sortField);
        queryParams.set("order_by", sortOrder);
        queryParams.set("limit", limit);
        const response = await fetch(
          `${LIVE_URL}/admin/list-payments-history?${queryParams.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();
        if (
          response.ok &&
          data.data &&
          Array.isArray(data.data.user_subscriptions)
        ) {
          setPayments(data.data.user_subscriptions);
          setFilteredPayments(data.data.user_subscriptions);
          setTotalPages(
            Math.ceil(data.data.pagination.totalCount / entriesPerPage)
          );
        } else {
          setPayments([]);
          setFilteredPayments([]);
          setTotalPages(1);
        }
      } catch (error) {
        console.error("Error fetching payments:", error);
        setPayments([]);
        setFilteredPayments([]);
        setTotalPages(1);
      } finally {
        // setLoading(false);
      }
    };

    if (!loading) {
      fetchPayment();
    }
  }, [currentPage, searchTerm, entriesPerPage, sortField, sortOrder]);

  useEffect(() => {
    if (payments) {
      const filtered = payments.filter(
        (payment) =>
          payment.revenuecat_transaction_id
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          payment.plan_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPayments(filtered);
    }
  }, [searchTerm, payments]);

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setEntriesPerPage(newLimit);
    setCurrentPage(1);
    setTotalPages(Math.ceil(filteredPayments.length / newLimit) || 1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousTwo = () => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleNextTwo = () => {
    if (currentPage !== totalPages) {
      setCurrentPage(totalPages);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    if (currentPage > 1) {
      pageNumbers.push(currentPage - 1);
    }
    pageNumbers.push(currentPage);
    if (currentPage < totalPages) {
      pageNumbers.push(currentPage + 1);
    }
    return pageNumbers;
  };
  const updateUrl = (search) => {
    const params = new URLSearchParams(location.search);
    if (search) {
      params.set("search", search);
    } else {
      params.delete("search");
    }
    navigate({ search: params.toString() });
  };

  useEffect(() => {
    updateUrl(searchTerm);
  }, [searchTerm]);
  return (
    <>
      <div className={`${styles.payment}`}>
        <header>
          <h2>Payment List Management</h2>
        </header>
        {/* entry section */}
        <section className={`${styles.section}`}>
          <div className={`${styles.user_entry}`}>
            <div className={`${styles.entries}`}>
              <label htmlFor="limit">Show</label>
              <select value={limit} onChange={handleLimitChange}>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              <label htmlFor="entries">entries</label>
            </div>
            <div className={`${styles.search}`}>
              {/* <label htmlFor="search">Search: </label> */}
              <IoMdSearch />
              <input
                type="search"
                id="search"
                value={searchTerm}
                placeholder="Search by transac..."
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* user information section */}
          <div className={`${styles.user_info}`}>
            <PaymentTable
              apiEndpoint={`${LIVE_URL}/admin/list-payments-history`}
              userName={searchTerm}
              payments={filteredPayments}
              setPayments={setPayments}
              rows={entriesPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              sortField={sortField}
              sortOrder={sortOrder}
              setSortField={setSortField}
              setSortOrder={setSortOrder}
              loading={loading}
              setLoading={setLoading}
              planName={planName}
              transactionId={transactionId}
              userId={userId}
            />
          </div>

          {/* pagination section */}
          <div className={`${styles.pagination}`}>
            <button onClick={handlePreviousTwo} disabled={currentPage === 1}>
              <FiChevronsLeft />
            </button>
            <button onClick={handlePrevious} disabled={currentPage === 1}>
              <FiChevronLeft />
            </button>
            {getPageNumbers().map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={currentPage === page ? styles.active : ""}
              >
                {page}
              </button>
            ))}
            <button onClick={handleNext} disabled={currentPage === totalPages}>
              <FiChevronRight />
            </button>
            <button
              onClick={handleNextTwo}
              disabled={
                currentPage === totalPages || currentPage === totalPages - 1
              }
            >
              <FiChevronsRight />
            </button>
          </div>
        </section>
      </div>
    </>
  );
};

export default Payment;
